/**
 * @fileoverview Workflow role badge
 *
 * The single way a workflow role is drawn: soft badge in the role's colour,
 * its optional icon inline, and its optional description as a hover tooltip.
 * Used by the roles manager, the workflow assignee picker, the team modal,
 * the create-job role assignment, and the flow view so a role looks the same
 * everywhere.
 *
 * Mounts its own `TooltipProvider` because this app has no global one.
 */

import type { TaskColor } from "@cvx/lib/taskColor.schema";
import type { WorkflowRoleIcon } from "@cvx/tasks/workflowRoles/schema";
import { Badge } from "@/ui/primitives";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "@/ui/composed/overlays";
import { cn } from "@/utils/misc";
import { roleIconComponent } from "./role-icons";

export interface RoleBadgeRole {
  name: string;
  color: TaskColor;
  icon?: WorkflowRoleIcon;
  description?: string;
}

export function RoleBadge({
  role,
  size,
  className,
}: {
  role: RoleBadgeRole;
  size?: "sm";
  className?: string;
}) {
  const Icon = roleIconComponent(role.icon);

  const badge = (
    <Badge
      color={role.color}
      variant="soft"
      size={size}
      className={cn("gap-1", className)}
    >
      {Icon && <Icon className="size-3 shrink-0" />}
      {role.name}
    </Badge>
  );

  if (!role.description) return badge;

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        {/* span wrapper: Badge renders a div, which cannot take the trigger
            ref cleanly inside flex rows without collapsing. */}
        <TooltipTrigger asChild>
          <span className="inline-flex">{badge}</span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[260px]">
          {role.description}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
